import { useState } from 'react';
import { createModel } from 'hox';
import { TablePaginationConfig } from 'antd/es/table/interface';
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE } from '@/utils/constants';
import { IPayableDetail, IPayableSearchFields, payableService } from './payableService';
import { useGlobalModel, transPgToPaginationTotal } from '@/model/globalModel';
import { convertRes2Blob } from '@/utils/exportFile';
import { ISearchPage } from '@/types/AppTypes';
import { getRandomStr } from '@/utils/utils';
import { useDictModel } from '@/views/base/dict/dictModel';

export const usePayableModel = createModel(function () {
	const {
		user: { shopCode },
		setLoadingInfo,
	} = useGlobalModel(({ user }) => [user]);
	const { getTreeNodeNameValue } = useDictModel();
	/**
	 * 列表
	 */
	//列表数据源
	const [dataSource, setDataSource] = useState<IPayableDetail[]>([]);
	//查询条件
	const [searchParam, setSearchParam] = useState<IPayableSearchFields>({});
	//分页参数
	const initPgCfg: TablePaginationConfig = {
		pageSize: DEFAULT_PAGE_SIZE,
		current: DEFAULT_FIRST_PAGE_NUM,
	};
	const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
	//表格多选id值
	const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	//收款明细弹框
	const [detailedDialog, setDetailedDialog] = useState<{ visible: boolean, row?: undefined | IPayableDetail }>({ visible: false });
	//列表查询
	const loadData = async (
		page: ISearchPage = initPgCfg,
		searchValue: IPayableSearchFields = searchParam
	) => {
		setLoading(true);
		const searchParams = {
			shopCode,
			...searchValue,
			pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
			pageNum: (page?.current || page?.pageNum) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
		};
		let { retData } = await payableService.page(searchParams);
		const { records, ...pg } = retData;
		setPgCfg(transPgToPaginationTotal(pg));
		setDataSource((records ?? []).map(item => ({ ...item, key: getRandomStr(), currency: getTreeNodeNameValue(item.currencyName) ? getTreeNodeNameValue(item.currencyName) : "￥" })));
		setLoading(false);
	};
	//设置搜索value
	const updateSearchParam = (updateParam: IPayableSearchFields) => {
		setSearchParam({ ...searchParam, ...updateParam });
	};

	//重置搜索参数
	const resetSearchParam = () => {
		setSearchParam({});
		setSelectedRowKeys([]);
		loadData(initPgCfg, {});
	};

	//导出
	const exportPayable = async (searchValue: IPayableSearchFields = searchParam) => {
		setLoadingInfo({ loadingVisible: true, txt: '快速导出中' });
		try {
			const searchParams = {
				shopCode,
				...searchValue,
				pageSize: -1,
				pageNum: 1,
			};

			let result = await payableService.export(searchParams);
			result && setLoadingInfo({ loadingVisible: false, txt: '' });
			convertRes2Blob(result);
		} catch {
			setLoadingInfo({ loadingVisible: false, txt: '' });
		}
	};

	//下载模板
	const downloadTpl = async () => {
		let result = await payableService.downloadTpl();
		convertRes2Blob(result);
	};

	return {
		/***field***/
		searchParam,
		dataSource,
		selectedRowKeys,
		pgCfg,
		loading,
		detailedDialog, setDetailedDialog,
		/***method***/
		setDataSource,
		setSelectedRowKeys,
		resetSearchParam,
		updateSearchParam,
		loadData,
		exportPayable,
		downloadTpl,
	};
});
